import { useState, useCallback } from 'react'
import type { ColumnDefinition, DataTableItem } from '../types'

interface UseDataTableColumnsOptions<T extends DataTableItem> {
  columns: ColumnDefinition<T>[]
  storageKey: string
}

interface UseDataTableColumnsReturn {
  visibleColumns: string[]
  isColumnVisible: (columnId: string) => boolean
  saveVisibleColumns: (columns: string[]) => void
}

function getDefaultVisibleColumns<T extends DataTableItem>(
  columns: ColumnDefinition<T>[]
): string[] {
  return columns.map(col => col.id)
}

/**
 * Persisted shape. `known` is what the table offered when the user last chose,
 * and it is the whole point of the object: without it a hidden column and a
 * column that did not exist yet are both simply absent from `visible`, so a
 * newly shipped column stays invisible to every user who ever touched their
 * settings. Storing what was on offer lets us tell those two cases apart.
 */
interface StoredColumns {
  visible: string[]
  known: string[]
}

function parseStored(raw: string): StoredColumns | null {
  const parsed = JSON.parse(raw)
  // Legacy format was a bare array of visible ids. Treat it as the set the user
  // was aware of, so columns added since then read as new and show up. A column
  // deliberately hidden before this migration reappears once; re-hiding it
  // writes the new shape and it stays hidden thereafter.
  if (Array.isArray(parsed)) return { visible: parsed, known: parsed }
  if (parsed && Array.isArray(parsed.visible) && Array.isArray(parsed.known)) {
    return { visible: parsed.visible, known: parsed.known }
  }
  return null
}

function loadVisibleColumns<T extends DataTableItem>(
  columns: ColumnDefinition<T>[],
  storageKey: string
): string[] {
  try {
    const raw = localStorage.getItem(storageKey)
    if (raw) {
      const stored = parseStored(raw)
      if (stored) {
        const requiredCols = columns.filter(c => c.required).map(c => c.id)
        const newCols = columns
          .filter(c => !stored.known.includes(c.id))
          .map(c => c.id)
        return [...new Set([...requiredCols, ...stored.visible, ...newCols])]
      }
    }
  } catch (e) {
    console.error('Failed to load column settings:', e)
  }
  return getDefaultVisibleColumns(columns)
}

export function useDataTableColumns<T extends DataTableItem>({
  columns,
  storageKey
}: UseDataTableColumnsOptions<T>): UseDataTableColumnsReturn {
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() =>
    loadVisibleColumns(columns, storageKey)
  )

  const isColumnVisible = useCallback((columnId: string) => {
    return visibleColumns.includes(columnId)
  }, [visibleColumns])

  const saveVisibleColumns = useCallback((newColumns: string[]) => {
    const requiredCols = columns.filter(c => c.required).map(c => c.id)
    const merged = [...new Set([...requiredCols, ...newColumns])]

    setVisibleColumns(merged)
    try {
      const payload: StoredColumns = { visible: merged, known: columns.map(c => c.id) }
      localStorage.setItem(storageKey, JSON.stringify(payload))
    } catch (e) {
      console.error('Failed to save column settings:', e)
    }
  }, [columns, storageKey])

  return {
    visibleColumns,
    isColumnVisible,
    saveVisibleColumns
  }
}
